
import withRouterParams from "../../../../withRouterParams";
import RoundedIconButton from "../../../../components/RoundedIconButton";
import { Refresh } from "@mui/icons-material";
import request from "../../../../request";
import swal from 'sweetalert';
import { Button, CircularProgress } from "@mui/material";
import CenteredMessage from "../../../../components/CenteredMessage";
import LabeledContainerProduct from "../../LabeledContainerProduct";
import EntityInfo from "../../EntityInfo";
import MapMarkers from "../../../../components/MapMarkers";
import LocationPicker from "../../../../components/LocationPicker";
import PillLabel from "../../../../components/PillLabel";
import { INSTALLATION_STATUS, USER_ROLE } from "../../../../../../shared/constants";
import AssignAgent from "./AssignAgent";
import StyledLink from "../../../../components/StyledLink";
import links from "../../../../links";
import DocumentsModal from "./DocumentsModal";
import LabeledContainer from "../../../../components/LabeledContainer";
import AttachmentList from "../../../../components/AttachmentList";
import { connect } from "react-redux";
import { hasIntersection } from "../../../../../../shared/utils";
import SerialNumbersInput from "../../../../components/SerialNumbersInput";
import SerialNumbers from "../../../../components/SerialNumbers";
import NavigationStateSaver from "../../../../components/NavigationStateSaver";


class _Installation extends NavigationStateSaver {

   state = {
      installation: null,
      loading: false,
      locationPickerOpen: false,
      assignAgentModalOpen: false,
      documentsModalOpen: false,
      serialNumbersModalOpen: false
   }

   openSerialNumbersModal = () => {
      this.setState({ serialNumbersModalOpen: true });
   }

   closeSerialNumbersModal = async (serial_numbers) => {

      if (serial_numbers) {
         const success = await this.setStatus(INSTALLATION_STATUS.IN_PROGRESS, { serial_numbers });
         if (!success)
            return; // so that the modal doesn't get closed;
      }

      this.setState({ serialNumbersModalOpen: false });

   }

   openDocumentsModal = () => {
      this.setState({ documentsModalOpen: true });
   }

   closeDocumentsModal = async (attachments) => {
      if (attachments) {
         const success = await this.setStatus(INSTALLATION_STATUS.INSTALLED, { attachments });

         if (!success)
            return; // do not close modal
      }

      this.setState({ documentsModalOpen: false });
      
   }

   openAssignAgentModal = () => {
      this.setState({ assignAgentModalOpen: true });
   }

   closeAssignAgentModal = (agent) => {
      const update = { assignAgentModalOpen: false }
      if (agent)
         update.installation = { ...this.state.installation, agent } // TODO: update using data from server

      this.setState(update);
   }

   openLocationPicker = () => {
      this.setState({ locationPickerOpen: true });
   }

   closeLocationPicker = async (location) => {

      if (location) {
         try {

            this.setState({ loading: true });

            const loanId = this.getId();
            await request.patch(`/api/technical/installations/${loanId}`, { location });

            this.setState({ installation: { ...this.state.installation, location } }); // TODO: update using data from server

         } catch (err) {
            swal("Error", err.message, "error");
            return;
         } finally {
            this.setState({ loading: false });
         }
      }

      this.setState({ locationPickerOpen: false });
   }


   setStatus = async (status, otherAttributes={}) => {

      try {

         this.setState({ loading: true });

         const payload = { status, ...otherAttributes };
         const loanId = this.getId();

         const res = await request.patch(`/api/technical/installations/${loanId}`, payload);
         this.setState({ installation: res.data });

         return true;

      } catch (err) {
         swal("Error", err.message, "error");
         return false;
      } finally {
         this.setState({ loading: false });
      }
   }

   fetchInstallation = async () => {

      try {

         this.setState({ loading: true });

         const loanId = this.getId();
         const res = await request.get(`/api/general/installations/${loanId}`);
         const installation = res.data;

         this.setState({ installation });

      } catch (err) {
         swal("Error", err.message, "error");
      } finally {
         this.setState({ loading: false });
      }
   }

   getId() {
      return this.props.params.id;
   }

   async componentDidMount() {
      await super.componentDidMount();
      if (!this.state.installation)
         this.fetchInstallation();
   }

   render() {

      let content;
      const { loading, installation } = this.state;
      let btnSetLocation, btnSetInProgress, btnSetInstalled;
      let btnAssignAgent;

      const canUpdate = hasIntersection(this.props.roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.TECHNICAL_MANAGER ]);

      if (loading) {
         content = <div className="h-full flex items-center justify-center">
            <CircularProgress />
         </div>
      } else if (!installation) {
         content = <div className="h-full">
            <CenteredMessage
               message={"Data not loaded"}
               onRefresh={this.fetchInstallation}
            />
         </div>
      } else {

         const { product, customer, location, status, agent } = installation;

         const markers = location ? [{
            location,
            caption: customer.address?.text
         }] : [];

         content = <div className="p-4 text-gray-600 flex flex-col gap-8">
            <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-2">
               <div className="flex items-center text-xl md:text-3xl gap-2">
                  Installation <b>#{this.getId().slice(-4)}</b>
                  <PillLabel color="secondary">
                     {status.toUpperCase()}
                  </PillLabel>
               </div>
               <div className="flex items-center gap-2">
                  {
                     agent && <>
                        Agent: <StyledLink to={links.agent(agent._id)}>
                           {agent.name} {agent.surname}
                        </StyledLink>
                     </>
                  }
               </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
               {product && <LabeledContainerProduct product={product} />}
               {customer && <EntityInfo info={customer} title="Customer" />}
            </div>

            {
               installation.attachments?.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <LabeledContainer label="Attachments" className="rounded-lg p-4">
                     <AttachmentList
                        attachments={installation.attachments}
                        variant="grid"
                     />
                  </LabeledContainer>
               </div>
            }

            <div className="rounded-lg overflow-hidden">
               <MapMarkers
                  markers={markers}
                  emptyMessage="No location set"
               />
            </div>

            {
               installation.serial_numbers?.length > 0 && <SerialNumbers
                  serialNumbers={installation.serial_numbers}
               />
            }
         </div>

         if (!location) {
            btnSetLocation = <Button
               className="rounded-full"
               variant="outlined"
               onClick={this.openLocationPicker}
               disabled={loading || !canUpdate}
            >
               SET LOCATION
            </Button>
         } else if (status === INSTALLATION_STATUS.PENDING) {
            btnSetInProgress = <Button
               className="rounded-full"
               variant="contained"
               color="primary"
               disabled={loading || !canUpdate}
               onClick={() => this.openSerialNumbersModal()}
            >
               SET IN PROGRESS
            </Button>
         } else if (status === INSTALLATION_STATUS.IN_PROGRESS) {
            btnSetInstalled = <Button
               className="rounded-full"
               variant="contained"
               disabled={loading || !canUpdate}
               onClick={this.openDocumentsModal}
            >
               SET INSTALLED
            </Button>
         }

         if (status !== INSTALLATION_STATUS.INSTALLED) {
            btnAssignAgent = <Button
               className="rounded-full"
               variant="outlined"
               disabled={loading || !canUpdate}
               onClick={this.openAssignAgentModal}
            >
               ASSIGN AGENT
            </Button>
         }
         
      }
      

      return <>
         <div className="h-full grid grid-rows-[1fr,auto]">
            <div className="overflow-auto">
               {content}            
            </div>
            <div className="border-t-[1px] p-2 flex justify-end gap-4">
               {btnAssignAgent}
               {btnSetLocation}
               {btnSetInProgress}
               {btnSetInstalled}
               <RoundedIconButton color="secondary" disabled={loading} onClick={this.fetchInstallation}>
                  <Refresh />
               </RoundedIconButton>
            </div>
         </div>
         {
            this.state.locationPickerOpen &&
            <LocationPicker
               onClose={this.closeLocationPicker}
            />
         }
         {
            this.state.assignAgentModalOpen &&  <AssignAgent
               onClose={this.closeAssignAgentModal}
               installationId={this.getId()}
            />
         }
         {
            this.state.documentsModalOpen && <DocumentsModal
               onClose={this.closeDocumentsModal}
            />
         }
         {
            this.state.serialNumbersModalOpen && <SerialNumbersInput
               product={this.state.installation.product}
               onClose={this.closeSerialNumbersModal}
            />
         }
      </>
   }

}

const mapStateToProps = state => ({
   roles: state.user?.roles || []
});

const Installation = connect(mapStateToProps)(withRouterParams(_Installation));

export default Installation;